import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { Progress } from '../../ui/progress';
import { useToast } from '../../ui/use-toast';
import { DatePickerWithRange } from '../../ui/date-range-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { apiService } from '../../../services/api';
import { 
  BarChart3, 
  Download,
  TrendingUp,
  TrendingDown,
  Calendar,
  Clock,
  Users,
  FileSpreadsheet,
  PieChart as PieChartIcon,
  Activity,
  AlertTriangle,
  CheckCircle,
  Filter,
  FileDown,
  Eye,
  Coffee,
  Timer,
  Target,
  Award,
  DollarSign,
  Package
} from 'lucide-react';
import { WorkEntry, User, Team, Project } from '../../../types';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area, ComposedChart } from 'recharts';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend } from 'date-fns';
import { ro } from 'date-fns/locale';
import { exportToExcel } from '../../../utils/exportUtils';
import * as XLSX from 'xlsx';

interface ReportsAnalyticsTabProps {
  workEntries: WorkEntry[];
  allUsers: User[];
  teams: Team[];
  projects: Project[];
  selectedDate: string;
  onDateChange: (date: string) => void;
  isLoading: boolean;
}

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

export const ReportsAnalyticsTab: React.FC<ReportsAnalyticsTabProps> = ({
  workEntries,
  allUsers,
  teams,
  projects,
  selectedDate,
  onDateChange,
  isLoading
}) => {
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: startOfWeek(new Date(), { locale: ro, weekStartsOn: 1 }), // Monday as first day
    to: endOfWeek(new Date(), { locale: ro, weekStartsOn: 1 })
  });
  const [selectedTeam, setSelectedTeam] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [selectedUserType, setSelectedUserType] = useState<string>('all');
  const [reportType, setReportType] = useState<'summary' | 'detailed' | 'performance' | 'trends'>('summary');
  const [rangeWorkEntries, setRangeWorkEntries] = useState<WorkEntry[]>([]);
  const [isLoadingRange, setIsLoadingRange] = useState(false);
  const { toast } = useToast();

  // Fetch work entries for the entire date range
  useEffect(() => {
    const fetchRangeData = async () => {
      if (!dateRange || !dateRange.from || !dateRange.to) return;
      
      setIsLoadingRange(true);
      try {
        const allEntries: WorkEntry[] = [];
        const currentDate = new Date(dateRange.from);
        const endDate = new Date(dateRange.to);
        
        // Fetch data for each day in the range
        while (currentDate <= endDate) {
          const dateStr = format(currentDate, 'yyyy-MM-dd');
          try {
            const response = await apiService.getWorkEntries(dateStr, undefined, { page: 1, pageSize: 1000 });
            if (response.data && Array.isArray(response.data)) {
              allEntries.push(...response.data);
            }
          } catch (err) {
            console.error(`Error fetching data for ${dateStr}:`, err);
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }
        
        setRangeWorkEntries(allEntries);
      } catch (error) {
        console.error('Error fetching range data:', error);
        toast({
          title: 'Eroare',
          description: 'Nu s-au putut încărca datele pentru perioada selectată',
          variant: 'destructive'
        });
      } finally {
        setIsLoadingRange(false);
      }
    };

    fetchRangeData();
  }, [dateRange, toast]);

  // Filter work entries based on date range and selections
  const filteredEntries = useMemo(() => {
    // Use rangeWorkEntries which contains data for the entire date range
    let filtered = rangeWorkEntries;

    if (selectedTeam !== 'all') {
      const teamUsers = allUsers.filter(u => u.teamId === parseInt(selectedTeam));
      const teamUserIds = teamUsers.map(u => u.id);
      filtered = filtered.filter(entry => teamUserIds.includes(entry.userId));
    }

    if (selectedProject !== 'all') {
      filtered = filtered.filter(entry => entry.projectId === parseInt(selectedProject));
    }

    if (selectedUserType !== 'all') {
      const filteredUsers = allUsers.filter(u => u.workerType === selectedUserType);
      const userIds = filteredUsers.map(u => u.id);
      filtered = filtered.filter(entry => userIds.includes(entry.userId));
    }

    return filtered;
  }, [rangeWorkEntries, selectedTeam, selectedProject, selectedUserType, allUsers]);

  // Calculate comprehensive analytics
  const analytics = useMemo(() => {
    // Basic metrics
    const totalHours = filteredEntries.reduce((sum, entry) => {
      if (!entry.startTime) return sum;
      const start = new Date(entry.startTime);
      const end = entry.endTime ? new Date(entry.endTime) : new Date();
      const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
      return sum + hours - (entry.totalBreakMinutes || 0) / 60;
    }, 0);

    const totalBreakHours = filteredEntries.reduce((sum, entry) => 
      sum + (entry.totalBreakMinutes || 0) / 60, 0);

    const uniqueWorkers = new Set(filteredEntries.map(e => e.userId)).size;
    const avgHoursPerWorker = uniqueWorkers > 0 ? totalHours / uniqueWorkers : 0;

    // Daily breakdown with enhanced statistics
    const dailyStats = dateRange && dateRange.from && dateRange.to 
      ? eachDayOfInterval({ start: dateRange.from, end: dateRange.to })
          .map(date => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const dayEntries = filteredEntries.filter(e => e.date === dateStr);
        
        const dayHours = dayEntries.reduce((sum, entry) => {
          if (!entry.startTime) return sum;
          const start = new Date(entry.startTime);
          const end = entry.endTime ? new Date(entry.endTime) : new Date();
          const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
          return sum + hours - (entry.totalBreakMinutes || 0) / 60;
        }, 0);

        const dayBreakMinutes = dayEntries.reduce((sum, entry) => 
          sum + (entry.totalBreakMinutes || 0), 0);

        const dayOvertimeHours = dayEntries.reduce((sum, entry) => {
          if (!entry.startTime) return sum;
          const start = new Date(entry.startTime);
          const end = entry.endTime ? new Date(entry.endTime) : new Date();
          const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60) - (entry.totalBreakMinutes || 0) / 60;
          return sum + Math.max(0, hours - 8);
        }, 0);

        return {
          date: dateStr,
          dayName: format(date, 'EEE', { locale: ro }),
          isWeekend: isWeekend(date),
          hours: dayHours,
          breakHours: dayBreakMinutes / 60,
          overtimeHours: dayOvertimeHours,
          workers: new Set(dayEntries.map(e => e.userId)).size,
          entries: dayEntries.length,
          avgHoursPerWorker: new Set(dayEntries.map(e => e.userId)).size > 0 
            ? dayHours / new Set(dayEntries.map(e => e.userId)).size 
            : 0
        };
      })
      : [];

    // Team performance
    const teamPerformance = teams.map(team => {
      const teamUsers = allUsers.filter(u => u.teamId === team.id);
      const teamUserIds = teamUsers.map(u => u.id);
      const teamEntries = filteredEntries.filter(e => teamUserIds.includes(e.userId));
      
      const teamHours = teamEntries.reduce((sum, entry) => {
        if (!entry.startTime) return sum;
        const start = new Date(entry.startTime);
        const end = entry.endTime ? new Date(entry.endTime) : new Date();
        const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
        return sum + hours - (entry.totalBreakMinutes || 0) / 60;
      }, 0);

      const avgHoursPerMember = teamUsers.length > 0 ? teamHours / teamUsers.length : 0;
      const productivity = avgHoursPerMember / (dailyStats.filter(d => !d.isWeekend).length * 8) * 100;

      return {
        name: team.name,
        totalHours: teamHours,
        members: teamUsers.length,
        avgHours: avgHoursPerMember,
        productivity: Math.min(productivity, 100),
        entries: teamEntries.length
      };
    }).filter(t => t.members > 0);

    // Project distribution
    const projectStats = projects.map(project => {
      const projectEntries = filteredEntries.filter(e => e.projectId === project.id);
      const projectHours = projectEntries.reduce((sum, entry) => {
        if (!entry.startTime) return sum;
        const start = new Date(entry.startTime);
        const end = entry.endTime ? new Date(entry.endTime) : new Date();
        const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
        return sum + hours - (entry.totalBreakMinutes || 0) / 60;
      }, 0);

      return {
        name: project.name,
        value: projectHours,
        workers: new Set(projectEntries.map(e => e.userId)).size,
        percentage: totalHours > 0 ? (projectHours / totalHours) * 100 : 0
      };
    }).filter(p => p.value > 0);

    // Worker type analysis
    const workerTypeStats = ['Angajat', 'Zilier'].map(type => {
      const typeUsers = allUsers.filter(u => u.workerType === type);
      const typeUserIds = typeUsers.map(u => u.id);
      const typeEntries = filteredEntries.filter(e => typeUserIds.includes(e.userId));
      
      const typeHours = typeEntries.reduce((sum, entry) => {
        if (!entry.startTime) return sum;
        const start = new Date(entry.startTime);
        const end = entry.endTime ? new Date(entry.endTime) : new Date();
        const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
        return sum + hours - (entry.totalBreakMinutes || 0) / 60;
      }, 0);

      return {
        name: type,
        hours: typeHours,
        workers: new Set(typeEntries.map(e => e.userId)).size,
        avgHours: typeUsers.length > 0 ? typeHours / typeUsers.length : 0
      };
    });

    // Overtime and alerts
    const overtimeWorkers = filteredEntries.reduce((acc, entry) => {
      if (!entry.startTime) return acc;
      const start = new Date(entry.startTime);
      const end = entry.endTime ? new Date(entry.endTime) : new Date();
      const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60) - (entry.totalBreakMinutes || 0) / 60;
      
      if (hours > 8) {
        if (!acc[entry.userId]) {
          acc[entry.userId] = { name: entry.userName, totalOvertime: 0, days: 0 };
        }
        acc[entry.userId].totalOvertime += hours - 8;
        acc[entry.userId].days += 1;
      }
      return acc;
    }, {} as Record<number, { name: string; totalOvertime: number; days: number }>);

    return {
      totalHours,
      totalBreakHours,
      uniqueWorkers,
      avgHoursPerWorker,
      productivity: (totalHours / (uniqueWorkers * dailyStats.filter(d => !d.isWeekend).length * 8)) * 100,
      dailyStats,
      teamPerformance,
      projectStats,
      workerTypeStats,
      overtimeWorkers: Object.values(overtimeWorkers),
      totalEntries: filteredEntries.length
    };
  }, [filteredEntries, teams, projects, allUsers, dateRange]);

  // Generate comprehensive report
  const handleGenerateReport = async (formatType: 'excel' | 'pdf') => {
    console.log('Starting report generation:', formatType);
    console.log('Filtered entries count:', filteredEntries.length);
    console.log('Analytics data:', analytics);
    
    if (!dateRange || !dateRange.from || !dateRange.to) {
      toast({
        title: 'Eroare',
        description: 'Selectează o perioadă pentru a genera raportul',
        variant: 'destructive'
      });
      return;
    }
    
    const fmtH = (hours: number): string => {
      const totalMin = Math.round(hours * 60);
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      return `${h} ore, ${m} min`;
    };

    try {
      if (formatType === 'excel') {
        const wb = XLSX.utils.book_new();

        // Summary sheet
        const summaryData = [
          ['Raport Sumar', ''],
          ['Perioada', `${format(dateRange.from, 'dd.MM.yyyy')} - ${format(dateRange.to, 'dd.MM.yyyy')}`],
          ['Total Ore Lucrate', fmtH(analytics.totalHours)],
          ['Total Ore Pauza', fmtH(analytics.totalBreakHours)],
          ['Muncitori Unici', analytics.uniqueWorkers],
          ['Media Ore/Muncitor', fmtH(analytics.avgHoursPerWorker)],
          ['Productivitate', `${analytics.productivity.toFixed(1)}%`],
          ['Total Inregistrari', analytics.totalEntries]
        ];
        const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(wb, wsSummary, 'Sumar');
        
        // Worker norms sheet
        const workerNormsData = allUsers
          .filter(user => {
            // Include only users who have work entries in the period
            return filteredEntries.some(e => e.userId === user.id);
          })
          .map(user => {
            const userEntries = filteredEntries.filter(e => e.userId === user.id);
            const totalDaysWorked = new Set(userEntries.map(e => e.date)).size;
            const totalMinutesWorked = userEntries.reduce((sum, entry) => {
              if (!entry.startTime) return sum;
              const minutes = ((entry.endTime ? new Date(entry.endTime) : new Date()).getTime() - 
                              new Date(entry.startTime).getTime()) / (1000 * 60);
              return sum + minutes;
            }, 0);
            
            const expectedMinutesPerDay = user.dailyWorkQuotaMinutes + user.dailyBreakMinutes;
            const expectedTotalMinutes = totalDaysWorked * expectedMinutesPerDay;
            const overtimeMinutes = Math.max(0, totalMinutesWorked - expectedTotalMinutes);
            
            return {
              'Muncitor': user.name,
              'Tip': user.workerType || 'Necunoscut',
              'Echipa': teams.find(t => t.id === user.teamId)?.name || '-',
              'Norma Zilnica (ore)': fmtH(user.dailyWorkQuotaMinutes / 60),
              'Pauza Configurata (min)': user.dailyBreakMinutes,
              'Total Zilnic Asteptat (ore)': fmtH(expectedMinutesPerDay / 60),
              'Zile Lucrate': totalDaysWorked,
              'Total Ore Lucrate': fmtH(totalMinutesWorked / 60),
              'Total Ore Asteptate': fmtH(expectedTotalMinutes / 60),
              'Ore Suplimentare': fmtH(overtimeMinutes / 60)
            };
          })
          .sort((a, b) => a['Muncitor'].localeCompare(b['Muncitor']));
        
        const wsNorms = XLSX.utils.json_to_sheet(workerNormsData);
        XLSX.utils.book_append_sheet(wb, wsNorms, 'Norme Muncitori');
        
        // Daily breakdown sheet
        const dailyData = analytics.dailyStats.map(day => ({
          'Data': format(new Date(day.date), 'dd.MM.yyyy'),
          'Zi': day.dayName,
          'Weekend': day.isWeekend ? 'Da' : 'Nu',
          'Ore Lucrate': fmtH(day.hours),
          'Muncitori': day.workers,
          'Inregistrari': day.entries
        }));
        const wsDaily = XLSX.utils.json_to_sheet(dailyData);
        XLSX.utils.book_append_sheet(wb, wsDaily, 'Zilnic');
        
        // Team performance sheet
        const teamData = analytics.teamPerformance.map(team => ({
          'Echipa': team.name,
          'Total Ore': fmtH(team.totalHours),
          'Membri': team.members,
          'Media Ore/Membru': fmtH(team.avgHours),
          'Productivitate': `${team.productivity.toFixed(1)}%`,
          'Inregistrari': team.entries
        }));
        const wsTeams = XLSX.utils.json_to_sheet(teamData);
        XLSX.utils.book_append_sheet(wb, wsTeams, 'Echipe');
        
        // Projects sheet
        const projectData = analytics.projectStats.map(project => ({
          'Proiect': project.name,
          'Ore Lucrate': fmtH(project.value),
          'Muncitori': project.workers,
          'Procent din Total': `${project.percentage.toFixed(1)}%`
        }));
        const wsProjects = XLSX.utils.json_to_sheet(projectData);
        XLSX.utils.book_append_sheet(wb, wsProjects, 'Proiecte');
        
        // Overtime sheet with approval details
        if (analytics.overtimeWorkers.length > 0) {
          // Fetch overtime records from API for approval details
          const overtimeRecords = await apiService.getOvertimes({ 
            date: format(dateRange.from, 'yyyy-MM-dd') 
          });
          
          const overtimeData = analytics.overtimeWorkers.map(worker => {
            // Find matching overtime records for this worker
            const workerOvertimes = overtimeRecords.overtimes?.filter(
              (or: any) => or.userName === worker.name
            ) || [];
            
            const approvedCount = workerOvertimes.filter((or: any) => or.isApproved).length;
            const deniedCount = workerOvertimes.filter((or: any) => or.isDenied).length;
            const pendingCount = workerOvertimes.filter((or: any) => !or.isApproved && !or.isDenied).length;
            
            // Get most recent approval info
            const latestApproval = workerOvertimes
              .filter((or: any) => or.isApproved || or.isDenied)
              .sort((a: any, b: any) => new Date(b.approvedAt || 0).getTime() - new Date(a.approvedAt || 0).getTime())[0];
            
            return {
              'Muncitor': worker.name,
              'Total Ore Suplimentare': fmtH(worker.totalOvertime),
              'Zile cu Ore Suplimentare': worker.days,
              'Aprobate': approvedCount,
              'Respinse': deniedCount,
              'In Asteptare': pendingCount,
              'Ultima Aprobare/Respingere De': latestApproval?.approvedByName || '-',
              'Data Ultimei Aprobari': latestApproval?.approvedAt ?
                format(new Date(latestApproval.approvedAt), 'dd.MM.yyyy HH:mm') : '-',
              'Motiv Overtime': (() => {
                if (latestApproval?.approvalComment?.includes('MOTIV OVERTIME:')) {
                  const parts = latestApproval.approvalComment.split('\n\nDETALII APROBARE:');
                  return parts[0]?.replace('MOTIV OVERTIME:', '').trim() || latestApproval?.reason || '-';
                }
                return latestApproval?.reason || '-';
              })(),
              'Detalii Aprobare': (() => {
                if (latestApproval?.approvalComment?.includes('DETALII APROBARE:')) {
                  const parts = latestApproval.approvalComment.split('\n\nDETALII APROBARE:');
                  return parts[1]?.trim() || '-';
                } else if (latestApproval?.approvalComment?.includes('MOTIV RESPINGERE:')) {
                  return latestApproval.approvalComment.replace('MOTIV RESPINGERE:', '').trim();
                }
                return latestApproval?.approvalComment || '-';
              })()
            };
          });
          const wsOvertime = XLSX.utils.json_to_sheet(overtimeData);
          XLSX.utils.book_append_sheet(wb, wsOvertime, 'Ore Suplimentare');
        }
        
        // Daily Overtime Approval Report
        try {
          const allOvertimeRecords: any[] = [];
          const currentDate = new Date(dateRange.from);
          const endDate = new Date(dateRange.to);
          
          while (currentDate <= endDate) {
            const dateStr = format(currentDate, 'yyyy-MM-dd');
            const dayOvertimes = await apiService.getOvertimes({ date: dateStr });
            if (dayOvertimes.overtimes) {
              allOvertimeRecords.push(...dayOvertimes.overtimes.map((or: any) => ({
                ...or,
                dateFormatted: dateStr
              })));
            }
            currentDate.setDate(currentDate.getDate() + 1);
          }
          
          if (allOvertimeRecords.length > 0) {
            const overtimeApprovalData = allOvertimeRecords.map(or => {
              // Parse the approval comment to extract reason and details
              let overtimeReason = '-';
              let approvalDetails = '-';
              
              if (or.approvalComment) {
                if (or.approvalComment.includes('MOTIV OVERTIME:')) {
                  const parts = or.approvalComment.split('\n\nDETALII APROBARE:');
                  overtimeReason = parts[0]?.replace('MOTIV OVERTIME:', '').trim() || '-';
                  approvalDetails = parts[1]?.trim() || or.approvalComment;
                } else if (or.approvalComment.includes('MOTIV RESPINGERE:')) {
                  approvalDetails = or.approvalComment.replace('MOTIV RESPINGERE:', '').trim();
                } else {
                  approvalDetails = or.approvalComment;
                }
              }
              
              // Use existing reason if no approval comment
              if (overtimeReason === '-' && or.reason) {
                overtimeReason = or.reason;
              }
              
              return {
                'Data': format(new Date(or.dateFormatted), 'dd.MM.yyyy'),
                'Muncitor': or.userName,
                'Ore Normale': fmtH(or.regularHours || 0),
                'Ore Suplimentare': fmtH(or.overtimeHours || 0),
                'Total Ore': fmtH(or.totalHours || 0),
                'Status': or.isApproved ? 'Aprobat' : or.isDenied ? 'Respins' : 'In Asteptare',
                'Aprobat/Respins De': or.approvedByName || '-',
                'Data Aprobarii': or.approvedAt ? format(new Date(or.approvedAt), 'dd.MM.yyyy HH:mm') : '-',
                'Motiv Ore Suplimentare': overtimeReason,
                'Detalii Decizie': approvalDetails
              };
            });
            
            const wsOvertimeApproval = XLSX.utils.json_to_sheet(overtimeApprovalData);
            XLSX.utils.book_append_sheet(wb, wsOvertimeApproval, 'Aprobări Ore Suplimentare');
          }
        } catch (error) {
          console.error('Error fetching overtime approval data:', error);
        }
        
        // Detailed entries sheet with new break system data
        const detailedData = filteredEntries.map(entry => {
          try {
            const user = allUsers.find(u => u.id === entry.userId);
            const totalMinutesAtWork = entry.startTime ? (
              ((entry.endTime ? new Date(entry.endTime) : new Date()).getTime() - 
              new Date(entry.startTime).getTime()) / (1000 * 60)
            ) : 0;
            const hoursAtWork = totalMinutesAtWork / 60;
            
            // Calculate actual work hours (total time - configured break)
            const actualWorkHours = entry.endTime && user ? 
              (totalMinutesAtWork - user.dailyBreakMinutes) / 60 : 
              hoursAtWork;
            
            // Check if overtime
            const expectedTotalMinutes = user ? user.dailyWorkQuotaMinutes + user.dailyBreakMinutes : 540; // 9 hours default
            const overtimeMinutes = Math.max(0, totalMinutesAtWork - expectedTotalMinutes);
            
            return {
              'Data': format(new Date(entry.date), 'dd.MM.yyyy'),
              'Muncitor': entry.userName,
              'Tip Muncitor': user?.workerType || 'Necunoscut',
              'Proiect': entry.projectName,
              'Locatie': entry.locationName,
              'Ora Start': entry.startTime ? format(new Date(entry.startTime), 'HH:mm') : '',
              'Ora Sfarsit': entry.endTime ? format(new Date(entry.endTime), 'HH:mm') : 'In lucru',
              'Total Ore la Locatie': fmtH(hoursAtWork),
              'Ore Lucru Efective': fmtH(actualWorkHours),
              'Pauza Configurata (min)': user?.dailyBreakMinutes || 60,
              'Norma Zilnica (ore)': fmtH(user ? user.dailyWorkQuotaMinutes / 60 : 8),
              'Total Norma + Pauza (ore)': fmtH(user ? (user.dailyWorkQuotaMinutes + user.dailyBreakMinutes) / 60 : 9),
              'Ore Suplimentare': fmtH(overtimeMinutes / 60),
              'Comentarii': entry.comments || '',
              'Descriere': entry.workDescription || ''
            };
          } catch (err) {
            console.error('Error formatting entry:', entry, err);
            const user = allUsers.find(u => u.id === entry.userId);
            return {
              'Data': entry.date,
              'Muncitor': entry.userName,
              'Tip Muncitor': user?.workerType || 'Necunoscut',
              'Proiect': entry.projectName,
              'Locatie': entry.locationName,
              'Ora Start': entry.startTime || '',
              'Ora Sfarsit': entry.endTime || 'In lucru',
              'Total Ore la Locatie': '0 ore, 0 min',
              'Ore Lucru Efective': '0 ore, 0 min',
              'Pauza Configurata (min)': user?.dailyBreakMinutes || 60,
              'Norma Zilnica (ore)': fmtH(user ? user.dailyWorkQuotaMinutes / 60 : 8),
              'Total Norma + Pauza (ore)': fmtH(user ? (user.dailyWorkQuotaMinutes + user.dailyBreakMinutes) / 60 : 9),
              'Ore Suplimentare': '0 ore, 0 min',
              'Comentarii': entry.comments || '',
              'Descriere': entry.workDescription || ''
            };
          }
        });
        const wsDetailed = XLSX.utils.json_to_sheet(detailedData);
        XLSX.utils.book_append_sheet(wb, wsDetailed, 'Detaliat');
        
        const filename = `raport-complet-${format(dateRange.from, 'yyyy-MM-dd')}-${format(dateRange.to, 'yyyy-MM-dd')}.xlsx`;
        XLSX.writeFile(wb, filename);
        
        toast({
          title: 'Export realizat',
          description: 'Raportul a fost generat cu succes',
        });
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut genera raportul',
        variant: 'destructive'
      });
    }
  };

  // Quick date range selections
  const handleQuickDateRange = (type: 'today' | 'week' | 'month' | 'lastMonth') => {
    const today = new Date();
    let from: Date, to: Date;

    switch (type) {
      case 'today':
        from = to = today;
        break;
      case 'week':
        from = startOfWeek(today, { locale: ro, weekStartsOn: 1 });
        to = endOfWeek(today, { locale: ro, weekStartsOn: 1 });
        break;
      case 'month':
        from = startOfMonth(today);
        to = endOfMonth(today);
        break;
      case 'lastMonth': {
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        from = startOfMonth(lastMonth);
        to = endOfMonth(lastMonth);
        break;
      }
    }

    setDateRange({ from, to });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Rapoarte și Analiză</h2>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              console.log('Export button clicked');
              handleGenerateReport('excel');
            }}
            className="flex items-center gap-2"
            disabled={isLoading || filteredEntries.length === 0}
            title={filteredEntries.length === 0 ? "Nu există date de exportat" : "Exportă raport complet în Excel"}
          >
            <FileSpreadsheet className="h-4 w-4" />
            Export Complet
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filtre Raport
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Range */}
            <div className="space-y-2">
              <Label>Perioada</Label>
              <DatePickerWithRange
                date={dateRange}
                setDate={(newDate) => {
                  if (newDate) {
                    setDateRange(newDate);
                  }
                }}
              />
              <div className="flex gap-1 mt-2">
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => handleQuickDateRange('today')}
                >
                  Azi
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => handleQuickDateRange('week')}
                >
                  Săptămână
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => handleQuickDateRange('month')}
                >
                  Luna
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => handleQuickDateRange('lastMonth')}
                >
                  Luna Trecută
                </Button>
              </div>
            </div>

            {/* Team Filter */}
            <div className="space-y-2">
              <Label>Echipă</Label>
              <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                <SelectTrigger>
                  <SelectValue placeholder="Toate echipele" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate echipele</SelectItem>
                  {teams.map(team => (
                    <SelectItem key={team.id} value={team.id.toString()}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Project Filter */}
            <div className="space-y-2">
              <Label>Proiect</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger>
                  <SelectValue placeholder="Toate proiectele" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate proiectele</SelectItem>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={project.id.toString()}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Worker Type Filter */}
            <div className="space-y-2">
              <Label>Tip Muncitor</Label>
              <Select value={selectedUserType} onValueChange={setSelectedUserType}>
                <SelectTrigger>
                  <SelectValue placeholder="Toți muncitorii" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toți muncitorii</SelectItem>
                  <SelectItem value="Angajat">Angajați</SelectItem>
                  <SelectItem value="Zilier">Zilieri</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* No data message */}
      {filteredEntries.length === 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Nu există date pentru perioada și filtrele selectate. Vă rugăm să ajustați criteriile de căutare.
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Ore Lucrate</p>
                <p className="text-2xl font-bold">{analytics.totalHours.toFixed(1)}h</p>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.avgHoursPerWorker.toFixed(1)}h medie/muncitor
                </p>
              </div>
              <Clock className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Muncitori Activi</p>
                <p className="text-2xl font-bold">{analytics.uniqueWorkers}</p>
                <p className="text-xs text-gray-500 mt-1">
                  din {allUsers.filter(u => u.role === 'worker').length} total
                </p>
              </div>
              <Users className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Productivitate</p>
                <p className="text-2xl font-bold">{analytics.productivity.toFixed(1)}%</p>
                <Progress value={analytics.productivity} className="mt-2 h-2" />
              </div>
              <Target className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Ore Suplimentare</p>
                <p className="text-2xl font-bold">{analytics.overtimeWorkers.length}</p>
                <p className="text-xs text-gray-500 mt-1">muncitori</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Report Type Tabs */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <Button
              size="sm"
              variant={reportType === 'summary' ? 'default' : 'outline'}
              onClick={() => setReportType('summary')}
            >
              Sumar
            </Button>
            <Button
              size="sm"
              variant={reportType === 'detailed' ? 'default' : 'outline'}
              onClick={() => setReportType('detailed')}
            >
              Detaliat
            </Button>
            <Button
              size="sm"
              variant={reportType === 'performance' ? 'default' : 'outline'}
              onClick={() => setReportType('performance')}
            >
              Performanță
            </Button>
            <Button
              size="sm"
              variant={reportType === 'trends' ? 'default' : 'outline'}
              onClick={() => setReportType('trends')}
            >
              Tendințe
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Report */}
          {reportType === 'summary' && (
            <div className="space-y-6">
              {/* Weekly Statistics Cards */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Statistici Săptămâna Curentă
                  {isLoadingRange && (
                    <span className="text-sm text-gray-500 ml-2">Se încarcă...</span>
                  )}
                </h3>
                {isLoadingRange ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3">
                    {[...Array(7)].map((_, index) => (
                      <Card key={index} className="animate-pulse">
                        <CardHeader className="pb-2">
                          <div className="h-4 bg-gray-200 rounded w-12"></div>
                          <div className="h-3 bg-gray-200 rounded w-16 mt-1"></div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <div className="h-3 bg-gray-200 rounded"></div>
                            <div className="h-3 bg-gray-200 rounded"></div>
                            <div className="h-3 bg-gray-200 rounded"></div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3">
                  {analytics.dailyStats.map((day, index) => {
                    const isToday = day.date === format(new Date(), 'yyyy-MM-dd');
                    const dayOfWeek = ['Lun', 'Mar', 'Mie', 'Joi', 'Vin', 'Sâm', 'Dum'];
                    
                    return (
                      <Card 
                        key={day.date} 
                        className={`${isToday ? 'ring-2 ring-blue-500' : ''} ${day.isWeekend ? 'bg-gray-50' : ''}`}
                      >
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-sm">
                              {dayOfWeek[index] || day.dayName}
                            </span>
                            {isToday && (
                              <Badge variant="default" className="text-xs">Azi</Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            {format(new Date(day.date), 'dd MMM', { locale: ro })}
                          </p>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <div className="space-y-1">
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-600">Ore lucrate</span>
                              <span className="text-sm font-bold text-blue-600">
                                {day.hours.toFixed(1)}h
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-600">Muncitori</span>
                              <span className="text-sm font-semibold">
                                {day.workers}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-600">Intrări</span>
                              <span className="text-sm">
                                {day.entries}
                              </span>
                            </div>
                            {day.breakHours > 0 && (
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-gray-600">Pauze</span>
                                <span className="text-sm text-gray-500">
                                  {day.breakHours.toFixed(1)}h
                                </span>
                              </div>
                            )}
                            {day.overtimeHours > 0 && (
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-gray-600">Overtime</span>
                                <span className="text-sm text-orange-600 font-semibold">
                                  +{day.overtimeHours.toFixed(1)}h
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* Progress bar for daily target (8 hours) */}
                          <div className="pt-2">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs text-gray-500">Target</span>
                              <span className="text-xs text-gray-500">
                                {Math.min((day.hours / (day.workers * 8) * 100), 100).toFixed(0)}%
                              </span>
                            </div>
                            <Progress 
                              value={Math.min((day.hours / (day.workers * 8) * 100), 100)} 
                              className="h-2"
                            />
                          </div>
                          
                          {/* Status indicator */}
                          <div className="pt-1">
                            {day.workers === 0 ? (
                              <Badge variant="secondary" className="w-full text-xs justify-center">
                                Fără activitate
                              </Badge>
                            ) : day.hours >= day.workers * 7 ? (
                              <Badge variant="default" className="w-full text-xs justify-center bg-green-500">
                                Productiv
                              </Badge>
                            ) : day.hours >= day.workers * 4 ? (
                              <Badge variant="default" className="w-full text-xs justify-center bg-yellow-500">
                                Moderat
                              </Badge>
                            ) : (
                              <Badge variant="destructive" className="w-full text-xs justify-center">
                                Scăzut
                              </Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                )}
                
                {/* Weekly Summary */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Total Ore Săptămână</p>
                          <p className="text-2xl font-bold text-blue-600">
                            {analytics.dailyStats.reduce((sum, day) => sum + day.hours, 0).toFixed(1)}h
                          </p>
                        </div>
                        <Clock className="h-8 w-8 text-blue-500 opacity-20" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Media Zilnică</p>
                          <p className="text-2xl font-bold text-green-600">
                            {(analytics.dailyStats.reduce((sum, day) => sum + day.hours, 0) / 
                              Math.max(analytics.dailyStats.filter(d => d.workers > 0).length, 1)).toFixed(1)}h
                          </p>
                        </div>
                        <Activity className="h-8 w-8 text-green-500 opacity-20" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Total Muncitori</p>
                          <p className="text-2xl font-bold text-purple-600">
                            {analytics.uniqueWorkers}
                          </p>
                        </div>
                        <Users className="h-8 w-8 text-purple-500 opacity-20" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Productivitate</p>
                          <p className="text-2xl font-bold text-orange-600">
                            {analytics.productivity.toFixed(0)}%
                          </p>
                        </div>
                        <Target className="h-8 w-8 text-orange-500 opacity-20" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Daily Activity Chart */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Grafic Activitate Zilnică</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics.dailyStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dayName" />
                    <YAxis yAxisId="left" orientation="left" stroke="#3b82f6" />
                    <YAxis yAxisId="right" orientation="right" stroke="#10b981" />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="hours" fill="#3b82f6" name="Ore lucrate" radius={[8, 8, 0, 0]} />
                    <Bar yAxisId="right" dataKey="workers" fill="#10b981" name="Muncitori" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Project Distribution */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Distribuție Proiecte</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics.projectStats}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percentage }) => `${name}: ${percentage.toFixed(1)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {analytics.projectStats.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => `${value.toFixed(1)}h`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Tip Muncitori</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics.workerTypeStats} layout="horizontal">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" />
                      <Tooltip />
                      <Bar dataKey="hours" fill="#8b5cf6" name="Ore lucrate" />
                      <Bar dataKey="workers" fill="#ec4899" name="Muncitori" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* Detailed Report */}
          {reportType === 'detailed' && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3">Data</th>
                    <th className="text-left p-3">Muncitor</th>
                    <th className="text-left p-3">Proiect</th>
                    <th className="text-left p-3">Locație</th>
                    <th className="text-left p-3">Început</th>
                    <th className="text-left p-3">Sfârșit</th>
                    <th className="text-left p-3">Ore</th>
                    <th className="text-left p-3">Pauză</th>
                    <th className="text-left p-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.slice(0, 50).map((entry) => (
                    <tr key={entry.id} className="border-b hover:bg-gray-50">
                      <td className="p-3">{format(new Date(entry.date), 'dd.MM.yyyy')}</td>
                      <td className="p-3">{entry.userName}</td>
                      <td className="p-3">{entry.projectName}</td>
                      <td className="p-3">{entry.locationName}</td>
                      <td className="p-3">
                        {entry.startTime ? format(new Date(entry.startTime), 'HH:mm') : '-'}
                      </td>
                      <td className="p-3">
                        {entry.endTime ? format(new Date(entry.endTime), 'HH:mm') : 'În lucru'}
                      </td>
                      <td className="p-3">
                        {entry.startTime ? (
                          ((entry.endTime ? new Date(entry.endTime) : new Date()).getTime() - 
                          new Date(entry.startTime).getTime()) / (1000 * 60 * 60) - 
                          (entry.totalBreakMinutes || 0) / 60
                        ).toFixed(1) : '0'}h
                      </td>
                      <td className="p-3">{entry.totalBreakMinutes || 0}m</td>
                      <td className="p-3">
                        <Badge variant={entry.isActive ? 'success' : 'secondary'}>
                          {entry.isActive ? 'Activ' : 'Finalizat'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredEntries.length > 50 && (
                <p className="text-center text-gray-500 py-4">
                  Afișate primele 50 din {filteredEntries.length} înregistrări
                </p>
              )}
            </div>
          )}

          {/* Performance Report */}
          {reportType === 'performance' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Performanță Echipe</h3>
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={analytics.teamPerformance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis yAxisId="left" orientation="left" stroke="#3b82f6" />
                  <YAxis yAxisId="right" orientation="right" stroke="#10b981" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="totalHours" fill="#3b82f6" name="Total ore" />
                  <Line yAxisId="right" type="monotone" dataKey="productivity" stroke="#10b981" name="Productivitate (%)" strokeWidth={3} />
                </ComposedChart>
              </ResponsiveContainer>

              {/* Team Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {analytics.teamPerformance.map((team) => (
                  <Card key={team.name}>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center justify-between">
                        {team.name}
                        <Badge variant={team.productivity >= 80 ? 'success' : team.productivity >= 60 ? 'warning' : 'destructive'}>
                          {team.productivity.toFixed(0)}%
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total ore:</span>
                          <span className="font-medium">{team.totalHours.toFixed(1)}h</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Membri:</span>
                          <span className="font-medium">{team.members}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Media/membru:</span>
                          <span className="font-medium">{team.avgHours.toFixed(1)}h</span>
                        </div>
                        <Progress value={team.productivity} className="mt-2" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Trends Report */}
          {reportType === 'trends' && (
            <div className="space-y-6">
              <Alert>
                <TrendingUp className="h-4 w-4" />
                <AlertDescription>
                  Analiza tendințelor pentru perioada {dateRange && dateRange.from && dateRange.to ? `${format(dateRange.from, 'dd.MM.yyyy')} - ${format(dateRange.to, 'dd.MM.yyyy')}` : 'Selectează perioada'}
                </AlertDescription>
              </Alert>

              {/* Overtime Workers */}
              {analytics.overtimeWorkers.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-orange-500" />
                    Muncitori cu Ore Suplimentare
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {analytics.overtimeWorkers.map((worker) => (
                      <Card key={worker.name} className="border-orange-200">
                        <CardContent className="pt-4">
                          <h4 className="font-semibold">{worker.name}</h4>
                          <div className="mt-2 space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Total ore suplimentare:</span>
                              <span className="font-medium text-orange-600">{worker.totalOvertime.toFixed(1)}h</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Zile cu overtime:</span>
                              <span className="font-medium">{worker.days}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Daily Trend Line */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Trend Ore Lucrate</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analytics.dailyStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dayName" />
                    <YAxis />
                    <Tooltip />
                    <Area 
                      type="monotone" 
                      dataKey="hours" 
                      stroke="#3b82f6" 
                      fill="#3b82f6" 
                      fillOpacity={0.3}
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};